import React from 'react';
import { motion, useTransform } from 'framer-motion';
import { Code, Lightbulb, Camera, FileText } from 'lucide-react';

const FloatingCards = ({ scrollProgress }) => {
  // Get screen width for precise calculations
  const getScreenWidth = () => {
    if (typeof window === 'undefined') return 1920;
    return window.innerWidth;
  };

  const screenWidth = getScreenWidth();
  
  // ============================================================================
  // P0 RULES (NEVER VIOLATE):
  // 1. Cards NEVER overlap hero text (both x AND y)
  // 2. Cards NEVER overlap each other (both x AND y)
  // 3. Optimize 2D space (horizontal + vertical) to fit cards
  // 4. On mobile (<768px): Move ALL cards out of viewport (not practical to fit)
  // ============================================================================
  
  // STEP 1: Mobile check - if mobile, hide all cards immediately
  const isMobile = screenWidth < 768;
  
  let cards;
  let scale;
  
  if (isMobile) {
    // Mobile: ALL cards out of viewport - completely hidden
    scale = 0.6;
    cards = [
      {
        id: 1,
        title: 'Divy Kairoth',
        subtitle: 'Product Builder',
        description: 'Building products, content & AI systems',
        type: 'profile',
        color: 'var(--accent-orange)',
        href: '#about',
        position: { bottom: -2000, right: -2000 },
      },
      {
        id: 2,
        title: 'Projects',
        icon: Code,
        description: 'Tally, wait.money, BrewClaw, Mindlessly',
        type: 'folder',
        color: 'var(--accent-green)',
        href: '#projects',
        position: { bottom: -2000, right: -2000 },
      },
      {
        id: 3,
        title: 'Ideas',
        icon: Lightbulb,
        description: 'Fighting OS, USBoard, Content Engine',
        type: 'folder',
        color: '#FFB84D',
        href: '#ideas',
        position: { bottom: -2000, right: -2000 },
      },
      {
        id: 4,
        title: 'Gallery',
        icon: Camera,
        description: 'Photography & visual work',
        type: 'folder',
        color: '#8B7FFF',
        href: '#gallery',
        position: { bottom: -2000, right: -2000 },
      },
      {
        id: 5,
        title: 'Blog',
        icon: FileText,
        description: '50 Shades of AI & more',
        type: 'folder',
        color: '#FF6B9D',
        href: '#blog',
        position: { bottom: -2000, right: -2000 },
      },
    ];
  } else {
    // STEP 2: For tablet/desktop - Calculate responsive scale
    // Reduced scale by ~10% as requested
    const getResponsiveScale = () => {
      if (screenWidth < 1024) return 0.50;  // Tablet (was 0.63)
      if (screenWidth < 1280) return 0.58;  // MacBook 13" (was 0.72)
      if (screenWidth < 1440) return 0.65;  // Small desktop (was 0.81)
      return 0.72;                           // Large desktop (was 0.9)
    };
    
    scale = getResponsiveScale();
    // Base dimensions reduced by 10% naturally via scale
    const cardWidth = 320 * scale;
    const cardHeight = 220 * scale;
    const minSpacing = 30 * scale;
    
    // STEP 3: Define hero text safe zone — keep cards fully to the right
    const textSafeZone = {
      xMin: 0,
      xMax: screenWidth * 0.55, // Text occupies left 55%
    };

    // STEP 4: Cards go in the rightmost portion of the screen
    const rightMargin = 20;
    const canFitTwoColumns = screenWidth >= 1280; // Only 2 columns on wide screens
    const canFitOneColumn = screenWidth >= 768;
    
    // Move column 1 cards down by 50px (change from 80 to 30)
    const col1BaseBottom = 30;
    
    // ALIGNMENT LOGIC:
    // 1. Calculate vertical center of Column 1 (3 cards)
    //    Height = 3 cards + 2 spaces
    const col1TotalHeight = cardHeight * 3 + minSpacing * 2;
    const col1CenterY = col1BaseBottom + (col1TotalHeight / 2);
    
    // 2. Center Column 2 (2 cards) around the SAME Y-axis point
    //    Height = 2 cards + 1 space
    const col2TotalHeight = cardHeight * 2 + minSpacing;
    const col2StartBottom = col1CenterY - (col2TotalHeight / 2);
    
    cards = [
    {
      id: 1,
      title: 'Divy Kairoth',
      subtitle: 'Product Builder',
      description: 'Building products, content & AI systems',
      type: 'profile',
      color: 'var(--accent-orange)',
      href: '#about',
      position: canFitOneColumn ? {
        bottom: col1BaseBottom,
        right: rightMargin,
      } : { bottom: -2000, right: -2000 },
    },
    {
      id: 2,
      title: 'Projects',
      icon: Code,
      description: 'BrewClaw, Mindlessly, Musq, Instagram',
      type: 'folder',
      color: 'var(--accent-green)',
      href: '#projects',
      position: canFitOneColumn ? {
        bottom: col1BaseBottom + cardHeight + minSpacing,
        right: rightMargin,
      } : { bottom: -2000, right: -2000 },
    },
    {
      id: 3,
      title: 'Ideas',
      icon: Lightbulb,
      description: 'Fighting OS, USBoard, Content Engine',
      type: 'folder',
      color: '#FFB84D',
      href: '#ideas',
      position: canFitOneColumn ? {
        bottom: col1BaseBottom + (cardHeight + minSpacing) * 2,
        right: rightMargin,
      } : { bottom: -2000, right: -2000 },
    },
    {
      id: 4,
      title: 'Gallery',
      icon: Camera,
      description: 'Photography & visual work',
      type: 'folder',
      color: '#8B7FFF',
      href: '#gallery',
      position: canFitTwoColumns ? {
        bottom: col2StartBottom,
        right: rightMargin + cardWidth + minSpacing,
      } : { bottom: -2000, right: -2000 },
    },
    {
      id: 5,
      title: 'Blog',
      icon: FileText,
      description: '50 Shades of AI & more',
      type: 'folder',
      color: '#FF6B9D',
      href: '#blog',
      position: canFitTwoColumns ? {
        bottom: col2StartBottom + cardHeight + minSpacing,
        right: rightMargin + cardWidth + minSpacing,
      } : { bottom: -2000, right: -2000 },
    },
  ];
  }

  // Use the cards and scale defined above
  const cardsList = cards;
  const responsiveScale = scale;
  const isTablet = screenWidth >= 768 && screenWidth < 1280;
  
  return (
    <div className="fixed inset-0 pointer-events-none tiles" style={{ zIndex: 20 }}>
      {cardsList.map((card) => {
        const Icon = card.icon;
        
        // Parallax drift during initial scroll (disabled on mobile since cards are hidden)
        const driftX = useTransform(scrollProgress, [0, 0.55], [0, isMobile ? 0 : -30 - card.id * 5]);
        const driftY = useTransform(scrollProgress, [0, 0.55], [0, isMobile ? 0 : -20 - card.id * 3]);
        
        // Opacity control
        // Mobile: Always hidden (cards out of viewport)
        // Tablet: Fade in after scroll
        // Desktop: Visible from start
        let opacity;
        if (isMobile) {
          opacity = useTransform(scrollProgress, [0, 1], [0, 0]); // Always hidden
        } else if (isTablet) {
          opacity = useTransform(scrollProgress, [0, 0.08, 0.15, 0.55, 0.65], [0, 0, 1, 0.3, 0]);
        } else {
          opacity = useTransform(scrollProgress, [0, 0.25, 0.55, 0.65], [1, 1, 0.3, 0]);
        }
        
        const cardScale = useTransform(scrollProgress, [0, 0.25], [1, 1]); 
        
        // Font sizes - minimum 20px enforced
        const getFontSize = (baseSize) => {
          const calculated = baseSize * responsiveScale;
          return Math.max(12, calculated); // Never below 12px
        };
        
        return (
          <motion.div
            key={card.id}
            initial={{
              opacity: 0, // Start hidden on all devices
              scale: 1,   // Start at normal size
            }}
            animate={{
              opacity: (isMobile || isTablet) ? 0 : 1, // Let scroll control on mobile/tablet
              scale: 1,
            }}
            style={{
              opacity,
              scale: cardScale,
              x: driftX,
              y: driftY,
              bottom: `${card.position.bottom}px`, // Don't scale negative positions
              right: `${card.position.right}px`,   // Don't scale negative positions
              display: isMobile ? 'none' : 'block', // Completely hide on mobile
              willChange: 'transform, opacity',
            }}
            transition={{
              duration: 0.8,
              delay: (isMobile || isTablet) ? 0 : card.id * 0.1, // No stagger on mobile/tablet
              ease: [0.16, 1, 0.3, 1],
            }}
            className="absolute pointer-events-auto cursor-pointer"
            onClick={() => { if (card.href) window.location.hash = card.href.replace('#', ''); }}
          >
            <div
              className="p-4 sm:p-5 md:p-6 lg:p-7 rounded-2xl md:rounded-3xl backdrop-blur-md shadow-2xl flex flex-col justify-between hover:scale-105 transition-transform"
              style={{
                background: `linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)`,
                border: `2px solid ${card.color}30`,
                width: `${320 * responsiveScale}px`,
                height: `${220 * responsiveScale}px`,
                minWidth: '160px',
                minHeight: '120px',
                overflow: 'hidden',
              }}
            >
              {card.type === 'profile' ? (
                <>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div
                      className="rounded-full flex items-center justify-center font-bold"
                      style={{ 
                        background: card.color, 
                        color: 'var(--bg-dark)',
                        fontSize: `${getFontSize(20)}px`,
                        width: `${Math.max(36, 64 * responsiveScale)}px`,
                        height: `${Math.max(36, 64 * responsiveScale)}px`,
                      }}
                    >
                      DK
                    </div>
                    <div>
                      <h3 
                        className="font-bold" 
                        style={{ 
                          color: 'var(--text-on-dark)',
                          fontSize: `${getFontSize(22)}px`,
                        }}
                      >
                        {card.title}
                      </h3>
                      <p 
                        className="opacity-70" 
                        style={{ 
                          color: 'var(--text-on-dark)',
                          fontSize: `${getFontSize(18)}px`,
                        }}
                      >
                        {card.subtitle}
                      </p>
                    </div>
                  </div>
                  <p 
                    className="opacity-80" 
                    style={{ 
                      color: 'var(--text-on-dark)',
                      fontSize: `${getFontSize(16)}px`,
                    }}
                  >
                    {card.description}
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div
                      className="rounded-xl flex items-center justify-center"
                      style={{ 
                        background: `${card.color}30`,
                        width: `${Math.max(32, 56 * responsiveScale)}px`,
                        height: `${Math.max(32, 56 * responsiveScale)}px`,
                      }}
                    >
                      <Icon 
                        size={Math.max(16, 28 * responsiveScale)} 
                        style={{ color: card.color }} 
                      />
                    </div>
                    <h3 
                      className="font-bold" 
                      style={{ 
                        color: 'var(--text-on-dark)',
                        fontSize: `${getFontSize(22)}px`,
                      }}
                    >
                      {card.title}
                    </h3>
                  </div>
                  <p 
                    className="opacity-70" 
                    style={{ 
                      color: 'var(--text-on-dark)',
                      fontSize: `${getFontSize(16)}px`,
                    }}
                  >
                    {card.description}
                  </p>
                </>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default FloatingCards;
